<template>
  <div class="flex flex-col my-4 border-black border-2 p-2 rounded-md">
    <div class="title">
      {{ title }}
    </div>

    <!-- En ejecucion -->
    <div v-if="isRunning" class="sync-status is-running">
      <div class="sync-status-head">
        <BaseMiniSpinner />
        <span class="sync-status-label">{{ $t("infoauto_sync.running") }}</span>
        <span class="sync-elapsed">{{ formatDuration(liveElapsed) }}</span>
      </div>
      <div class="sync-detail">
        {{
          $t("infoauto_sync.started_by", {
            user: lastUpdate.user,
            date: $options.filters.dateWithHours(lastUpdate.dateStart)
          })
        }}
      </div>
      <template v-if="lastUpdate.previousDurationSeconds">
        <div class="sync-progress">
          <div
            class="sync-progress-bar"
            :style="{ width: estimatedProgress + '%' }"
          ></div>
        </div>
        <div class="sync-detail">
          {{
            $t("infoauto_sync.previous_took", {
              duration: formatDuration(lastUpdate.previousDurationSeconds)
            })
          }}
        </div>
      </template>
      <div v-if="looksStuck" class="sync-warning">
        <i class="mdi mdi-alert"></i>
        {{ $t("infoauto_sync.looks_stuck") }}
      </div>
      <button
        class="sync-cancel-btn"
        :disabled="cancelling"
        @click="cancelProcess"
      >
        <i class="mdi mdi-stop-circle-outline"></i>
        {{
          cancelling
            ? $t("infoauto_sync.cancelling")
            : $t("infoauto_sync.cancel")
        }}
      </button>
    </div>

    <!-- Terminado -->
    <div v-else-if="lastUpdate" class="sync-status" :class="'is-' + lastStatus">
      <div class="sync-status-head">
        <i :class="statusIcon"></i>
        <span class="sync-status-label">{{
          $t("infoauto_sync.status_" + lastStatus)
        }}</span>
        <span class="sync-elapsed">{{
          $t("infoauto_sync.took", {
            duration: formatDuration(lastUpdate.elapsedSeconds)
          })
        }}</span>
      </div>
      <div class="sync-detail">
        {{ lastUpdate.dateStart | dateWithHours }} →
        {{ lastUpdate.dateEnd | dateWithHours }} · {{ lastUpdate.user }}
      </div>
      <div v-if="lastUpdateMessage" class="sync-message">
        {{ lastUpdateMessage }}
      </div>
    </div>

    <div class="w-full pt-6">
      <button
        class="btn-dark-button"
        v-if="!isRunning && !starting"
        @click="updateParts"
      >
        {{ cta }}
      </button>
    </div>
  </div>
</template>
<script>
import {
  updateAllParts,
  infoautoLastResult,
  cancelInfoautoProcess
} from "@/api/store/Infoauto.js";

// Sin referencia previa, a partir de cuanto se sugiere que quedo colgado
const STUCK_WITHOUT_REFERENCE_SECONDS = 3600;

export default {
  data() {
    return {
      lastUpdate: null,
      lastUpdateMessage: "",
      lastStatus: "success",
      // El request de update queda abierto mientras corre el sync
      starting: false,
      cancelling: false,
      fetchedAt: 0,
      now: Date.now(),
      pollInterval: null,
      tickInterval: null
    };
  },
  props: {
    type: {
      type: String,
      required: true
    },
    title: {
      type: String,
      required: true
    },
    cta: {
      type: String,
      required: true
    }
  },
  computed: {
    isRunning() {
      return !!(this.lastUpdate && this.lastUpdate.running);
    },
    // Lo que informo el server mas lo que paso desde esa respuesta
    liveElapsed() {
      if (!this.lastUpdate) return 0;
      const sinceFetch = Math.max(0, (this.now - this.fetchedAt) / 1000);
      return (this.lastUpdate.elapsedSeconds || 0) + sinceFetch;
    },
    estimatedProgress() {
      const reference = this.lastUpdate.previousDurationSeconds;
      return Math.min(95, (this.liveElapsed / reference) * 100);
    },
    looksStuck() {
      const reference = this.lastUpdate.previousDurationSeconds;
      const limit = reference ? reference * 3 : STUCK_WITHOUT_REFERENCE_SECONDS;
      return this.liveElapsed > limit;
    },
    statusIcon() {
      return {
        success: "mdi mdi-check-circle",
        error: "mdi mdi-alert-circle",
        cancelled: "mdi mdi-cancel"
      }[this.lastStatus];
    }
  },
  mounted() {
    this.getLastResult();
    this.pollInterval = setInterval(() => {
      if (this.isRunning || this.starting) {
        this.getLastResult();
      }
    }, 5000);
    this.tickInterval = setInterval(() => {
      this.now = Date.now();
    }, 1000);
  },
  beforeDestroy() {
    clearInterval(this.pollInterval);
    clearInterval(this.tickInterval);
  },
  methods: {
    formatDuration(totalSeconds) {
      const seconds = Math.floor(totalSeconds || 0);
      const h = Math.floor(seconds / 3600);
      const m = Math.floor((seconds % 3600) / 60);
      const s = seconds % 60;
      if (h > 0) return `${h}h ${m}m`;
      if (m > 0) return `${m}m ${s}s`;
      return `${s}s`;
    },
    getLastResult() {
      return infoautoLastResult(this.type)
        .then(res => {
          this.lastUpdate = res || null;
          this.fetchedAt = Date.now();
          this.now = this.fetchedAt;
          if (!this.lastUpdate || this.lastUpdate.running) return;
          let parsed = null;
          try {
            parsed = JSON.parse(this.lastUpdate.response);
          } catch (error) {
            parsed = null;
          }
          this.lastUpdateMessage =
            parsed && parsed.message
              ? parsed.message
              : this.lastUpdate.response;
          this.lastStatus =
            parsed && ["error", "cancelled"].includes(parsed.status)
              ? parsed.status
              : "success";
        })
        .catch(() => {
          this.$toast.open({
            message: `Error getting last results for '${this.type}'`,
            type: "error",
            position: "top-right"
          });
        });
    },
    updateParts() {
      this.starting = true;
      // El proceso se registra al empezar: se muestra en cuanto aparece
      setTimeout(() => this.getLastResult(), 1500);
      updateAllParts(this.type)
        .then(res => {
          this.$toast.open({
            message: res.message,
            type:
              res.status === "error"
                ? "error"
                : res.status === "cancelled"
                ? "warning"
                : "success",
            position: "top-right"
          });
        })
        .catch(err => {
          // Si el request se corta (timeout) el sync sigue corriendo en el server
          if (this.isRunning) return;
          this.$toast.open({
            message: `Error updating products: ${err.message}`,
            type: "error",
            position: "top-right"
          });
        })
        .finally(() => {
          this.starting = false;
          this.getLastResult();
        });
    },
    cancelProcess() {
      if (!window.confirm(this.$t("infoauto_sync.cancel_confirm"))) return;
      this.cancelling = true;
      cancelInfoautoProcess(this.type)
        .then(() => {
          this.$toast.open({
            message: this.$t("infoauto_sync.cancelled_toast"),
            type: "success",
            position: "top-right"
          });
        })
        .catch(err => {
          this.$toast.open({
            message:
              (err.response &&
                err.response.data &&
                err.response.data.message) ||
              err.message,
            type: "error",
            position: "top-right"
          });
        })
        .finally(() => {
          this.cancelling = false;
          this.getLastResult();
        });
    }
  }
};
</script>
<style scoped>
.sync-status {
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
}
.sync-status.is-running {
  border-color: #93c5fd;
  background: #eff6ff;
}
.sync-status.is-success {
  border-color: #86efac;
  background: #f0fdf4;
}
.sync-status.is-error {
  border-color: #fca5a5;
  background: #fef2f2;
}
.sync-status.is-cancelled {
  border-color: #d1d5db;
  background: #f3f4f6;
}
.sync-status-head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
}
.is-success .sync-status-head {
  color: #15803d;
}
.is-error .sync-status-head {
  color: #b91c1c;
}
.is-running .sync-status-head {
  color: #1d4ed8;
}
.is-cancelled .sync-status-head {
  color: #4b5563;
}
.sync-elapsed {
  margin-left: auto;
  font-variant-numeric: tabular-nums;
}
.sync-detail {
  margin-top: 4px;
  font-size: 13px;
  color: #4b5563;
}
.sync-message {
  margin-top: 6px;
  font-size: 13px;
  color: #111827;
}
.sync-progress {
  margin-top: 8px;
  height: 6px;
  border-radius: 9999px;
  background: #dbeafe;
  overflow: hidden;
}
.sync-progress-bar {
  height: 100%;
  background: #3b82f6;
  transition: width 1s linear;
}
.sync-warning {
  margin-top: 8px;
  font-size: 13px;
  color: #92400e;
}
.sync-cancel-btn {
  margin-top: 10px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  border: 1px solid #fca5a5;
  background: #fff;
  color: #b91c1c;
  font-size: 13px;
  font-weight: 600;
}
.sync-cancel-btn:disabled {
  opacity: 0.6;
}
</style>
